/**
 * De kolommen van een export: de opgegeven volgorde eerst (alleen wat echt
 * voorkomt), daarna elke sleutel die niet genoemd is. Gedeeld met de
 * Excel-export, zodat CSV en Excel dezelfde kolommen in dezelfde volgorde geven.
 *
 * @param {Array<Record<string, any>>} rows - De rijen; sleutels worden kolommen
 * @param {string[]} [headerOrder] - Gewenste kolomvolgorde
 * @returns {string[]}
 */
export const exportHeader = (rows, headerOrder) => {
    const union = new Set()
    rows.forEach((row) => Object.keys(row || {}).forEach((key) => union.add(key)))

    return headerOrder && headerOrder.length
        ? headerOrder.filter((h) => union.has(h)).concat([...union].filter((h) => !headerOrder.includes(h)))
        : Array.from(union)
}

/**
 * Zet een lijst objecten om naar CSV-tekst.
 *
 * Volgt de CSV-afspraak (RFC 4180): een cel met een komma, aanhalingsteken of
 * regeleinde staat tussen aanhalingstekens, en een aanhalingsteken binnenin
 * wordt verdubbeld. Het eerdere recept gebruikte `JSON.stringify` per cel; dat
 * ontsnapte met een backslash (Excel toont dan `Kees zei \"ja\"`) en liet een
 * lijst ongequote staan, waardoor de komma's daarin extra kolommen maakten en
 * de hele rij verschoof.
 *
 * Eén plek voor het CSV-recept, zodat de exports niet uit elkaar gaan lopen.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @param {Array<Record<string, any>>} rows - De rijen; sleutels worden kolommen
 * @param {string[]} [headerOrder] - Gewenste kolomvolgorde; ontbrekende kolommen komen erachter
 * @returns {string} CSV-tekst, of een lege tekst zonder rijen
 */
const toCSV = (rows, headerOrder) => {
    if (!rows || rows.length === 0) return ''

    const header = exportHeader(rows, headerOrder)

    const quote = (text) => `"${String(text).replace(/"/g, '""')}"`

    const cell = (value) => {
        if (value === undefined || value === null) return '""'
        // Lijsten worden leesbaar samengevoegd in één cel; als JSON zou een
        // gebruiker in Excel `["Joop","Kim"]` zien.
        if (Array.isArray(value)) return quote(value.join(', '))
        if (typeof value === 'number' || typeof value === 'boolean') return String(value)
        if (value instanceof Date) return quote(value.toISOString())
        return quote(value)
    }

    return [
        header.map(quote).join(','),
        ...rows.map((row) => header.map((key) => cell((row || {})[key])).join(',')),
    ].join('\n')
}

export default toCSV
